<template>
  <v-navigation-drawer v-model="active" right temporary fixed width="600">
    <v-container class="pa-10">
      <v-row class="fill-height" align="center">
        <v-col>
          <v-row>
            <v-col class="py-0">
              <h4 class="headline blue-grey--text text--darken-3">
                Crear Producto
              </h4>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <validation-observer ref="form">
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12">
              <v-form>
                <validation-provider
                  name="firstName"
                  v-slot="{ errors }"
                  rules="required"
                >
                  <v-text-field
                    v-model="fields.name"
                    label="Nombre"
                    :error-messages="errors"
                  />
                </validation-provider>

                <validation-provider
                  name="category"
                  v-slot="{ errors }"
                  rules="required"
                >
                  <product-category-select
                    v-model="fields.category"
                    label="Categoría"
                    :error-messages="errors"
                  />
                </validation-provider>

                <v-select
                  label="Tipo"
                  v-model="fields.type"
                  :items="typeOptions"
                />

                <v-row class="pb-12" v-if="fields.type === 'combo'">
                  <v-col cols="12">
                    <v-data-table
                      hide-default-footer
                      hide-default-header
                      :items="fields.comboItems"
                      :headers="comboItemsHeaders"
                      class="elevation-0"
                    >
                      <template v-slot:headers>
                        <td style="width: 100px">Cantidad</td>
                        <td>Producto</td>
                        <td></td>
                      </template>

                      <template v-slot:item.quantity="{ item }">
                        {{ item.quantity }}
                      </template>

                      <template v-slot:item.product="{ item }">
                        {{ item.name }}
                      </template>

                      <template v-slot:item.actions="{ item }">
                        <v-btn text icon @click="remove(item)"
                          ><v-icon small>mdi-close</v-icon></v-btn
                        >
                      </template>

                      <template v-slot:body.append>
                        <tr>
                          <td style="width: 96px">
                            <v-text-field
                              ref="quantityField"
                              label="Cantidad"
                              v-model="itemQuantityInput"
                              @focus="$event.target.select()"
                            ></v-text-field>
                          </td>

                          <td>
                            <product-select
                              v-model="itemProductInput"
                              :products="products"
                            />
                          </td>

                          <td>
                            <v-btn
                              text
                              icon
                              ref="addButton"
                              color="primary"
                              @click="add"
                              :disabled="
                                !itemProductInput || !itemQuantityInput
                              "
                            >
                              <v-icon>mdi-plus</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>

                <v-text-field
                  v-model="fields.presentation"
                  label="Presentación"
                  counter
                ></v-text-field>

                <v-textarea
                  v-model="fields.description"
                  label="Descripción"
                  counter
                ></v-textarea>

                <v-file-input
                  class="mb-4"
                  v-model="fields.thumbnail"
                  label="Vista previa"
                  prepend-icon=""
                  append-icon="mdi-camera-outline"
                  hint="Recomendado: 200 x 150 px"
                  persistent-hint
                  show-size
                />

                <v-switch v-model="fields.isWholesaler" label="Mayorista" />

                <v-row align="center">
                  <v-col class="flex-grow-0">
                    <v-icon>mdi-currency-usd</v-icon>
                  </v-col>
                  <v-col class="px-0">
                    <span class="title">Precio</span>
                  </v-col>
                </v-row>

                <v-text-field
                  v-model="fields.price"
                  label="Precio"
                  prefix="$"
                ></v-text-field>

                <v-select
                  label="Impuesto"
                  v-model="fields.tax"
                  :items="taxOptions"
                  prefix="%"
                />
              </v-form>
            </v-col>
          </v-row>

          <v-row align="center" class="section-title">
            <v-col class="flex-grow-0">
              <v-icon>mdi-package-variant-closed</v-icon>
            </v-col>
            <v-col class="px-0">
              <span class="title">Stock</span>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-switch v-model="hasStock" label="Stockeable" />

              <v-text-field
                v-if="hasStock"
                label="Corte de Venta"
                v-model="stockFields.stopSellingStockLevel"
                suffix="Unidades"
              ></v-text-field>

              <v-text-field
                v-if="hasStock"
                label="Stock Mínimo"
                v-model="stockFields.minimumStockLevel"
              ></v-text-field>

              <v-switch
                v-if="hasStock"
                v-model="stockFields.perishable"
                label="Perecedero"
              >
              </v-switch>
            </v-col>
          </v-row>
        </v-form>
      </validation-observer>
    </v-container>

    <template v-slot:append>
      <v-row class="ma-4">
        <v-spacer></v-spacer>

        <v-btn color="green darken-1" text @click="onCancelSelected"
          >Cancelar</v-btn
        >
        <v-btn color="primary" @click="onSaveSelected">Guardar</v-btn>
      </v-row>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ProductCategorySelect from "@/components/Product/ProductCategorySelect";
import ProductSelect from "@/components/Product/ProductSelect";
import { createComboProductInputMapper } from "./mappers/CreateComboProductInputMapper";

export default {
  components: {
    ProductCategorySelect,
    ValidationProvider,
    ValidationObserver,
    ProductSelect,
  },

  data() {
    return {
      active: false,

      valid: false,

      categories: [],

      hasStock: false,

      fields: {
        name: "",
        category: null,
        type: "product",
        comboItems: [],
        price: "",
        tax: "",
        presentation: "",
        description: "",
        isWholesaler: false,
        thumbnail: null,
      },

      stockFields: {
        minimumStockLevel: 0,
        stopSellingStockLevel: 0,
        perishable: false,
      },

      taxOptions: [
        { text: "0", value: 0.0 },
        { text: "21", value: 21.0 },
        { text: "10.5", value: 10.5 },
      ],

      typeOptions: [
        { text: "Normal", value: "product" },
        { text: "Combo", value: "combo" },
      ],

      comboItemsHeaders: [
        { text: "", value: "quantity" },
        { text: "", value: "product" },
        { text: "", value: "actions" },
      ],

      itemQuantityInput: 0,
      itemProductInput: null,
    };
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    stock() {
      return this.$store.getters["stock/getByProductId"](this.product.id);
    },

    products() {
      return this.$store.getters["products/getAll"]();
    },
  },

  watch: {
    active(val) {
      this.$emit("input", val);
    },

    value(val) {
      this.active = val;
    },
  },

  methods: {
    async onSaveSelected() {
      let result = await this.$refs.form.validate();
      if (!result) {
        return;
      }

      if (this.fields.type === "combo") {
        const payload = createComboProductInputMapper(this.fields);
        try {
          await this.$store.dispatch("products/createCombo", {
            data: payload,
          });
          this.$store.dispatch("app/setStatusMessage", "Producto creado");
          this.active = false;
        } catch (err) {
          let formErrors = {};

          for (let errField in err.errors) {
            formErrors[errField] = this.$t(err.errors[errField]);
          }

          this.$refs.form.setErrors(formErrors);
        }
        return;
      }

      let payload = { ...this.fields };
      if (this.fields.category) {
        payload.category = this.fields.category.id;
      }

      let stockPayload = { ...this.stockFields };

      try {
        let product = await this.$store.dispatch("products/create", {
          data: payload,
        });

        if (this.hasStock) {
          stockPayload.product = product.id;
          await this.$store.dispatch("stock/create", { data: stockPayload });
        }

        this.$store.dispatch("app/setStatusMessage", "Producto creado");

        this.active = false;
      } catch (err) {
        let formErrors = {};

        for (let errField in err.errors) {
          formErrors[errField] = this.$t(err.errors[errField]);
        }

        this.$refs.form.setErrors(formErrors);
      }
    },

    onCancelSelected(value) {
      this.active = false;
    },

    get(id) {
      for (let p of this.fields.comboItems) {
        if (p.id === id) {
          return p;
        }
      }

      return null;
    },

    add() {
      let existingItem = this.get(this.itemProductInput.id);
      if (existingItem) {
        existingItem.quantity =
          parseInt(existingItem.quantity) + parseInt(this.itemQuantityInput);
      } else {
        this.fields.comboItems.push({
          id: this.itemProductInput.id,
          name: this.itemProductInput.name,
          quantity: this.itemQuantityInput,
          price: this.itemProductInput.price,
          itemProductInput: {
            id: this.itemProductInput.id,
            thumbnail: this.itemProductInput.thumbnail,
          },
        });
      }

      // reset
      this.itemQuantityInput = 1;
      this.itemProductInput = null;

      requestAnimationFrame(() => {
        return this.$refs.quantityField.focus();
      });
    },

    remove(item) {
      let idx = this.fields.comboItems.indexOf(item);
      this.fields.comboItems.splice(idx, 1);

      this.$emit("input", this.fields.comboItems);
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  h4.headline {
    font-size: 20px !important;
  }

  .main-image {
    background-color: white;
    margin-right: 20px;

    img {
      width: 200px;
    }
  }

  .v-btn {
    margin: 0;
  }
}
</style>
